import axios, {
  AxiosHeaders,
  HttpStatusCode
} from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import EmailAddress from "common/values/email-address/email-address";
import Guid from "common/values/guid/guid";
import Password from "common/values/password/password";
import UserOpenEntityAgreement from "legal-entities/entities/entity-agreement/user-open-entity-agreement";
import UserNetworkConnectionProfile from "users/entities/user-network-connection/user-network-connection-profile";
import NetworkInvitationInfo from "users/entities/user-network-invitation/network-invitation-info";
import LoginWithEmailAPIRequest from "users/entities/user/api/request-contracts/login-with-email-api-request";
import ResetPasswordConfirmationAPIRequest from "users/entities/user/api/request-contracts/reset-password-confirmation-api-request";
import ResetPasswordRequestAPIRequest from "users/entities/user/api/request-contracts/reset-password-request-api-request";
import AuthInfoAPIResponse, { AuthContext } from "users/entities/user/api/response-contracts/auth-info-api-response";
import User from "users/entities/user/user";
import { InvalidCredentialsError } from "users/errors";
import UserAuthToken from "users/values/user-auth-token/user-auth-token";
import UserBookmark from "users/values/user-bookmark/user-bookmark";
import UserCredentials from "users/values/user-credentials/user-credentials";
import UserEntityInfo from "users/values/user-entity-info/user-entity-info";
import UserVendorViewAPIRequest from "./request-contracts/user-vendor-view-api-request";


export type AuthResponse = {
  user: User;
  context: AuthContext;
  authToken: UserAuthToken;
  legalEntities: UserEntityInfo[];
  openEntityAgreements: UserOpenEntityAgreement[];
  networkConnections: UserNetworkConnectionProfile[];
  networkInvitations: NetworkInvitationInfo[];
  bookmarkedIndividuals: UserBookmark[];
  bookmarkedCompanies: UserBookmark[];
  bookmarkedTeams: UserBookmark[];
}

export default class AuthEmailAPIService {

  private static jsonHeaders(): AxiosHeaders {
    return Object.assign(new AxiosHeaders(), {
      "Content-Type": "application/json"
    });
  }

  static async login(credentials: UserCredentials, rememberMe: boolean = false):
    Promise<AuthResponse> {
    try {
      const url = new URL("/users/login", AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        new LoginWithEmailAPIRequest(credentials, rememberMe),
        {
          headers: this.jsonHeaders()
        }
      );

      const authInfo: AuthInfoAPIResponse = Object.assign(
        new AuthInfoAPIResponse(),
        response.data
      );

      const user = authInfo.deserializeUser();
      const context = authInfo.deserializeContext();
      const authToken = authInfo.deserializeAuthToken();
      const legalEntities = authInfo.deserializeUserEntityInfo();
      const openEntityAgreements = authInfo.deserializeOpenEntityAgreements();
      const networkConnections = authInfo.deserializeNetworkConnections();
      const networkInvitations = authInfo.deserializeNetworkInvitations();
      const bookmarkedIndividuals = authInfo.deserializeBookmarkedIndividuals();
      const bookmarkedCompanies = authInfo.deserializeBookmarkedCompanies();
      const bookmarkedTeams = authInfo.deserializeBookmarkedTeams();

      return {
        user,
        context,
        authToken,
        legalEntities,
        openEntityAgreements,
        networkConnections,
        networkInvitations,
        bookmarkedIndividuals,
        bookmarkedCompanies,
        bookmarkedTeams
      };
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new UserAPIServiceError("login", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          console.warn(error.message);
          throw new InvalidCredentialsError();
        case HttpStatusCode.Unauthorized:
          throw new InvalidCredentialsError();
        default:
          throw new UserAPIServiceError("login", error);
      }
    }
  }

  static async requestPasswordReset(email: EmailAddress): Promise<void> {
    try {
      const url = new URL("/users/request-password-reset", AttorneyHubAPIService.apiBaseUrl);
      await axios.post(
        url.toString(),
        new ResetPasswordRequestAPIRequest(email),
        {
          headers: this.jsonHeaders()
        }
      );
    } catch (error) {
      if (
        axios.isAxiosError(error) && 
        error.response?.status === HttpStatusCode.BadRequest
      ) {
        throw new InvalidPasswordResetEmailError(email.value);
      } else {
        throw new UserAPIServiceError("requestPasswordReset", error);
      }
    }
  }

  static async confirmPasswordReset(password: Password, userId: Guid, token: string): Promise<AuthResponse> {
    try {
      const url = new URL("/users/confirm-password-reset", AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        new ResetPasswordConfirmationAPIRequest(password, userId, token),
        {
          headers: this.jsonHeaders()
        }
      );
      const authInfo: AuthInfoAPIResponse = Object.assign(
        new AuthInfoAPIResponse(),
        response.data
      );

      const user = authInfo.deserializeUser();
      const context = authInfo.deserializeContext();
      const authToken = authInfo.deserializeAuthToken();
      const legalEntities = authInfo.deserializeUserEntityInfo();
      const openEntityAgreements = authInfo.deserializeOpenEntityAgreements();
      const networkConnections = authInfo.deserializeNetworkConnections();
      const networkInvitations = authInfo.deserializeNetworkInvitations();
      const bookmarkedIndividuals = authInfo.deserializeBookmarkedIndividuals();
      const bookmarkedCompanies = authInfo.deserializeBookmarkedCompanies();
      const bookmarkedTeams = authInfo.deserializeBookmarkedTeams();

      return {
        user,
        context,
        authToken,
        legalEntities,
        openEntityAgreements,
        networkConnections,
        networkInvitations,
        bookmarkedIndividuals,
        bookmarkedCompanies,
        bookmarkedTeams
      };
    } catch (error) {
      if (
        axios.isAxiosError(error) && 
        error.response?.status === HttpStatusCode.BadRequest
      ) {
        console.warn(error.message);
      }
      throw new UserAPIServiceError("confirmPasswordReset", error);
    }
  }

  static async updateUserVendorView(viewingAsVendor: boolean, authToken?: Readonly<UserAuthToken>): Promise<void> {
    if (!authToken)
      throw new Error("No auth token provided for updateUserVendorView");
    try {
      const url = new URL("/users/user-vendor-view", AttorneyHubAPIService.apiBaseUrl);
      await axios.patch(
        url.toString(),
        new UserVendorViewAPIRequest(viewingAsVendor), {
        headers:
          Object.assign(new AxiosHeaders(), {
            "Authorization": `Bearer ${authToken.value}`
          })
      });
    } catch (error) {
      if (
        axios.isAxiosError(error) && 
        error.response?.status === HttpStatusCode.Unauthorized
      ) {
        throw new ExpiredTokenError();
      }
      throw new UserAPIServiceError("updateUserVendorView", error);
    }
  }
}

export class ExpiredTokenError extends Error {
  constructor() {
    super("Expired token");
  }
}
export class InvalidPasswordResetEmailError extends Error {
  constructor(email: string) {
    super("Invalid email address: " + email);
  }
}

export class UserAPIServiceError extends Error {
  method: string;
  error: unknown;
  constructor(method: string, error: unknown) {
    super((error as Error)?.message ?? "An error occurred");
    this.method = method;
    this.error = error;
  }
}
